import type { LexicalEditor } from 'lexical'

import type { ErrorInfo, ReactNode } from 'react'

import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import RedoIcon from '@mui/icons-material/Redo'
import TitleIcon from '@mui/icons-material/Title'
import UndoIcon from '@mui/icons-material/Undo'
import { Alert, Box, FormHelperText, IconButton, MenuItem, Paper, Select, Stack, Typography } from '@mui/material'
import { CodeNode } from '@lexical/code'
import { $generateNodesFromDOM } from '@lexical/html'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isRootNode,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  type LexicalNode,
  type TextNode,
} from 'lexical'
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode, REMOVE_LIST_COMMAND } from '@lexical/list'
import { $createHeadingNode, $isHeadingNode, HeadingNode, QuoteNode } from '@lexical/rich-text'
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS, type TextMatchTransformer } from '@lexical/markdown'
import { $setBlocksType } from '@lexical/selection'
import { LinkNode } from '@lexical/link'

import { InlineImagePlugin } from './editor/InlineImagePlugin'
import { $createImageNode, $isImageNode, ImageNode } from './editor/ImageNode'
import { escapeHtmlAttribute, escapeUnsupportedHtmlTags, shouldImportAsHtml } from '../utils/htmlTags'

type RichTextEditorProps = {
  error?: boolean
  helperText?: string
  label: string
  onChange: (value: string) => void
  onPasteImage: (file: File) => Promise<string>
  placeholder: string
  required?: boolean
  value: string
}

type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3'

const IMAGE_MARKDOWN_TRANSFORMER: TextMatchTransformer = {
  type: 'text-match',
  dependencies: [ImageNode],
  trigger: ')',
  importRegExp: /!\[([^\]]*)\]\(([^)\s]+(?:\s+"[^"]*")?)\)/,
  regExp: /!\[([^\]]*)\]\(([^)\s]+(?:\s+"[^"]*")?)\)$/,
  export: (node) => {
    if (!$isImageNode(node)) {
      return null
    }

    if (node.__width || node.__height) {
      const attributes = [
        `src="${escapeHtmlAttribute(node.__src)}"`,
        `alt="${escapeHtmlAttribute(node.__altText || '')}"`,
        node.__width ? `width="${node.__width}"` : '',
        node.__height ? `height="${node.__height}"` : '',
      ].filter(Boolean)

      return `<img ${attributes.join(' ')} />`
    }

    return `![${node.__altText || ''}](${node.__src})`
  },
  replace: (textNode: TextNode, match) => {
    const [, altText = '', rawSource = ''] = match
    const src = rawSource.replace(/\s+"[^"]*"$/, '').trim()
    textNode.replace($createImageNode({ altText, src }))
    return $createTextNode('')
  },
}

const HTML_IMAGE_TRANSFORMER: TextMatchTransformer = {
  type: 'text-match',
  dependencies: [ImageNode],
  trigger: '>',
  importRegExp: /<img\b[^>]*>/i,
  regExp: /<img\b[^>]*>$/i,
  replace: (textNode: TextNode, match) => {
    const payload = parseHtmlImage(match[0])
    if (!payload) {
      return
    }

    textNode.replace($createImageNode(payload))
    return $createTextNode('')
  },
}

const EDITOR_TRANSFORMERS = [IMAGE_MARKDOWN_TRANSFORMER, HTML_IMAGE_TRANSFORMER, ...TRANSFORMERS]

function containsHtml(value: string) {
  return /<[^>]+>/.test(value)
}

function normalizeRootImportNodes(nodes: LexicalNode[]) {
  const normalized: LexicalNode[] = []
  let paragraph = $createParagraphNode()

  const flushParagraph = () => {
    if (!paragraph.isEmpty()) {
      normalized.push(paragraph)
      paragraph = $createParagraphNode()
    }
  }

  nodes.forEach((node) => {
    if ($isElementNode(node) || $isDecoratorNode(node)) {
      flushParagraph()
      normalized.push(node)
      return
    }

    paragraph.append(node)
  })

  flushParagraph()
  return normalized
}

function parseDimension(value: string | null) {
  if (!value) {
    return undefined
  }

  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function parseHtmlImage(markup: string) {
  const parsedDocument = new DOMParser().parseFromString(markup, 'text/html')
  const image = parsedDocument.querySelector('img')
  const src = image?.getAttribute('src')
  if (!image || !src) {
    return null
  }

  return {
    altText: image.getAttribute('alt') || '',
    height: parseDimension(image.getAttribute('height')) || parseDimension(image.style.height),
    src,
    width: parseDimension(image.getAttribute('width')) || parseDimension(image.style.width),
  }
}

function normalizeMarkdownValue(markdown: string) {
  return markdown.replace(/\n{3,}/g, '\n\n').trim()
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext()
  const [blockType, setBlockType] = useState<BlockType>('paragraph')
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        editor.getEditorState().read(() => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) {
            return
          }

          setIsBold(selection.hasFormat('bold'))
          setIsItalic(selection.hasFormat('italic'))

          const anchorNode = selection.anchor.getNode()
          if ($isRootNode(anchorNode)) {
            setBlockType('paragraph')
            return
          }

          const topLevelNode = anchorNode.getTopLevelElement()
          if ($isHeadingNode(topLevelNode)) {
            const tag = topLevelNode.getTag()
            setBlockType(tag === 'h1' || tag === 'h2' || tag === 'h3' ? tag : 'paragraph')
          } else {
            setBlockType('paragraph')
          }
        })

        return false
      },
      1,
    )
  }, [editor])

  useEffect(() => {
    return editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload)
        return false
      },
      1,
    )
  }, [editor])

  useEffect(() => {
    return editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload)
        return false
      },
      1,
    )
  }, [editor])

  function setBlock(nextBlockType: BlockType) {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) {
        return
      }

      if (nextBlockType === 'paragraph') {
        $setBlocksType(selection, () => $createParagraphNode())
      } else {
        $setBlocksType(selection, () => $createHeadingNode(nextBlockType))
      }
    })
    setBlockType(nextBlockType)
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ borderBottom: '1px solid #d8e0ea', p: 1, flexWrap: 'wrap' }}>
      <IconButton size="small" disabled={!canUndo} onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}>
        <UndoIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" disabled={!canRedo} onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}>
        <RedoIcon fontSize="small" />
      </IconButton>
      <Select
        size="small"
        value={blockType}
        onChange={(event) => setBlock(event.target.value as BlockType)}
        sx={{ minWidth: 132, mx: 0.5, '& .MuiSelect-select': { py: 0.75 } }}
      >
        <MenuItem value="paragraph">
          <Stack direction="row" spacing={1} alignItems="center">
            <HorizontalRuleIcon fontSize="small" />
            <span>Paragraph</span>
          </Stack>
        </MenuItem>
        <MenuItem value="h1">
          <Stack direction="row" spacing={1} alignItems="center">
            <TitleIcon fontSize="small" />
            <span>Heading 1</span>
          </Stack>
        </MenuItem>
        <MenuItem value="h2">
          <Stack direction="row" spacing={1} alignItems="center">
            <TitleIcon fontSize="small" />
            <span>Heading 2</span>
          </Stack>
        </MenuItem>
        <MenuItem value="h3">
          <Stack direction="row" spacing={1} alignItems="center">
            <TitleIcon fontSize="small" />
            <span>Heading 3</span>
          </Stack>
        </MenuItem>
      </Select>
      <IconButton
        size="small"
        color={isBold ? 'primary' : 'default'}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
      >
        <FormatBoldIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color={isItalic ? 'primary' : 'default'}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
      >
        <FormatItalicIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}>
        <FormatListBulletedIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}>
        <FormatListNumberedIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)}>
        <HorizontalRuleIcon fontSize="small" />
      </IconButton>
    </Stack>
  )
}

type MarkdownSyncPluginProps = {
  onChange: (markdown: string) => void
  value: string
}

function MarkdownSyncPlugin({ onChange, value }: MarkdownSyncPluginProps) {
  const [editor] = useLexicalComposerContext()
  const lastExternalValueRef = useRef<string | null>(null)

  useEffect(() => {
    if (value === lastExternalValueRef.current) {
      return
    }

    editor.update(() => {
      const root = $getRoot()
      root.clear()

      if (!value.trim()) {
        root.append($createParagraphNode())
      } else if (containsHtml(value) && shouldImportAsHtml(value)) {
        const parsedDocument = new DOMParser().parseFromString(escapeUnsupportedHtmlTags(value), 'text/html')
        const nodes = normalizeRootImportNodes($generateNodesFromDOM(editor, parsedDocument))
        if (nodes.length) {
          root.append(...nodes)
        } else {
          root.append($createParagraphNode())
        }
      } else {
        $convertFromMarkdownString(value, EDITOR_TRANSFORMERS, root)
      }
    })

    lastExternalValueRef.current = value
  }, [editor, value])

  const handleChange = useCallback((editorState: ReturnType<LexicalEditor['getEditorState']>) => {
    editorState.read(() => {
      const nextValue = normalizeMarkdownValue($convertToMarkdownString(EDITOR_TRANSFORMERS, $getRoot()))
      lastExternalValueRef.current = nextValue
      onChange(nextValue)
    })
  }, [onChange])

  return <OnChangePlugin onChange={handleChange} />
}

function EditorPlaceholder({ placeholder }: { placeholder: string }) {
  return (
    <Box
      sx={{
        color: '#94a3b8',
        left: 16,
        lineHeight: 1.5,
        pointerEvents: 'none',
        position: 'absolute',
        top: 16,
      }}
    >
      {placeholder}
    </Box>
  )
}

class RichTextEditorErrorBoundary extends Component<
  { children: ReactNode; label: string },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[RichTextEditor] ${this.props.label} failed to load`, error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error">
          The rich text editor could not load this content. Please contact an administrator.
        </Alert>
      )
    }

    return this.props.children
  }
}

function RichTextEditorInner({
  error = false,
  helperText,
  label,
  onChange,
  onPasteImage,
  placeholder,
  required = false,
  value,
}: RichTextEditorProps) {
  const [pasteError, setPasteError] = useState('')
  const [editorError, setEditorError] = useState<Error | null>(null)
  const initialConfig = useMemo(() => ({
    namespace: `article-rich-editor-${label}`,
    nodes: [CodeNode, HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, ImageNode],
    onError: (editorError: Error) => {
      setEditorError(editorError)
    },
    theme: {
      heading: {
        h1: 'Lexical__h1',
        h2: 'Lexical__h2',
        h3: 'Lexical__h3',
      },
      list: {
        listitem: 'Lexical__listItem',
        nested: {
          listitem: 'Lexical__nestedListItem',
        },
        ol: 'Lexical__orderedList',
        ul: 'Lexical__unorderedList',
      },
      paragraph: 'Lexical__paragraph',
      text: {
        bold: 'Lexical__textBold',
        italic: 'Lexical__textItalic',
      },
    },
  }), [label])

  const handleUploadError = useCallback((message: string) => {
    setPasteError(message)
    onChange(value)
    window.setTimeout(() => {
      setPasteError('')
      console.error(message)
    }, 5000)
  }, [onChange, value])

  if (editorError) {
    throw editorError
  }

  return (
    <Box>
      <Typography
        component="label"
        sx={{
          color: error ? 'error.main' : 'text.primary',
          display: 'block',
          fontSize: 14,
          fontWeight: 500,
          mb: 1,
        }}
      >
        {label}
        {required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          borderColor: error ? 'error.main' : '#d8e0ea',
          borderRadius: 2,
          overflow: 'hidden',
          '& .editor-input': {
            minHeight: 176,
            outline: 'none',
            px: 2,
            py: 2,
            position: 'relative',
          },
          '& .editor-input > *:first-of-type': {
            marginTop: 0,
          },
          '& .editor-input p': {
            lineHeight: 1.5,
            margin: 0,
          },
          '& .editor-input h1, & .editor-input h2, & .editor-input h3, & .editor-input ul, & .editor-input ol, & .editor-input blockquote': {
            marginTop: 0,
          },
        }}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <ToolbarPlugin />
          <Box sx={{ position: 'relative' }}>
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={<EditorPlaceholder placeholder={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </Box>
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <MarkdownShortcutPlugin transformers={EDITOR_TRANSFORMERS} />
          <AutoFocusPlugin />
          <MarkdownSyncPlugin value={value} onChange={onChange} />
          <InlineImagePlugin onPasteImage={onPasteImage} onUploadError={handleUploadError} />
        </LexicalComposer>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Supports headings, bold, italic, lists, markdown shortcuts, and pasted images.
      </Typography>
      {(pasteError || helperText) && (
        <FormHelperText error={error || Boolean(pasteError)} sx={{ whiteSpace: 'pre-line' }}>
          {pasteError || helperText}
        </FormHelperText>
      )}
    </Box>
  )
}

export function RichTextEditor(props: RichTextEditorProps) {
  return (
    <RichTextEditorErrorBoundary label={props.label}>
      <RichTextEditorInner {...props} />
    </RichTextEditorErrorBoundary>
  )
}
